import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useRef, useState } from 'react';
import { Button, FormControl, FormHelperText, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Icon } from '@livable/saas-react-components';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
const CELL_BORDER = '1px solid #e0e0e0';
export const MapUnitNamesSection = observer(_ref => {
  var _store$homeDashboardS, _store$homeDashboardS2;
  let dataRef = _ref.dataRef,
    onValidChange = _ref.onValidChange,
    initialUnitMappings = _ref.initialUnitMappings;
  const store = useStore();
  const _useState = useState(initialUnitMappings || {}),
    _useState2 = _slicedToArray(_useState, 2),
    unitMappings = _useState2[0],
    setUnitMappings = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    csvFile = _useState4[0],
    setCsvFile = _useState4[1];
  const _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    csvHeaders = _useState6[0],
    setCsvHeaders = _useState6[1];
  const _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    csvRows = _useState8[0],
    setCsvRows = _useState8[1];
  const _useState9 = useState(''),
    _useState0 = _slicedToArray(_useState9, 2),
    unitNameColumn = _useState0[0],
    setUnitNameColumn = _useState0[1];
  const _useState1 = useState([]),
    _useState10 = _slicedToArray(_useState1, 2),
    csvUnitOptions = _useState10[0],
    setCsvUnitOptions = _useState10[1];
  const _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    isDragging = _useState12[0],
    setIsDragging = _useState12[1];
  const _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    showErrors = _useState14[0],
    setShowErrors = _useState14[1];
  const fileInputRef = useRef(null);
  const unitNames = ((_store$homeDashboardS = (_store$homeDashboardS2 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.selectedPropertyDetails()) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.details) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.unitSetup) === null || _store$homeDashboardS2 === void 0 ? void 0 : _store$homeDashboardS2.units) !== null && _store$homeDashboardS !== void 0 ? _store$homeDashboardS : []).map(u => u.name).filter(Boolean).sort((a, b) => a.localeCompare(b, undefined, {
    numeric: true
  }));

  // Partial mapping is allowed — units left empty simply won't be mapped.
  // Only the CSV unit-name column selection is required (when a file is uploaded).
  const isColumnValid = !csvFile || !!unitNameColumn;
  const isValid = isColumnValid;

  // Sync validity to parent synchronously (same pattern as dataRef)
  onValidChange === null || onValidChange === void 0 || onValidChange(isValid);

  // Sync data + validate fn to parent ref
  if (dataRef) {
    dataRef.current = {
      unitMappings,
      validate: () => {
        setShowErrors(true);
        return isValid;
      }
    };
  }
  const parseCsvFile = async file => {
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').trim()).filter(Boolean);
      const rows = lines.slice(1).map(line => line.split(',').map(v => v.trim().replace(/^"|"$/g, '').trim()));
      setCsvHeaders(headers);
      setCsvRows(rows);
    } catch (_unused) {
      setCsvHeaders([]);
      setCsvRows([]);
    }
  };
  const getUnitOptions = (rows, headers, column) => {
    const colIdx = headers.indexOf(column);
    if (colIdx === -1) return [];
    return [...new Set(rows.map(row => row[colIdx]).filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, {
      numeric: true
    }));
  };
  const handleFileSelect = async file => {
    setCsvFile(file);
    setUnitNameColumn('');
    setCsvUnitOptions([]);
    setUnitMappings({});
    await parseCsvFile(file);
  };
  const handleFileInputChange = async e => {
    var _e$target$files;
    const file = (_e$target$files = e.target.files) === null || _e$target$files === void 0 ? void 0 : _e$target$files[0];
    if (file) await handleFileSelect(file);
  };
  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = async e => {
    var _e$dataTransfer$files;
    e.preventDefault();
    setIsDragging(false);
    const file = (_e$dataTransfer$files = e.dataTransfer.files) === null || _e$dataTransfer$files === void 0 ? void 0 : _e$dataTransfer$files[0];
    if (file) await handleFileSelect(file);
  };
  const handleRemoveFile = () => {
    setCsvFile(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setUnitNameColumn('');
    setCsvUnitOptions([]);
    setUnitMappings({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleColumnChange = column => {
    setUnitNameColumn(column);
    setUnitMappings({});
    setCsvUnitOptions(getUnitOptions(csvRows, csvHeaders, column));
  };
  return /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 24
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    style: {
      fontWeight: 600,
      marginBottom: 4
    }
  }, "Map unit names"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    color: "light",
    style: {
      marginBottom: 4
    }
  }, "Map the unit names in Livable to the names of the units in your CSV file"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    style: {
      marginBottom: 16
    }
  }, "Changed this after reads were saved? Recalculate on Meter details to remove charges from units that are no longer mapped. Newly mapped units won't be added to a cycle that already has saved rows."), /*#__PURE__*/React.createElement("div", {
    style: {
      border: "1px dashed ".concat(isDragging ? '#1976d2' : '#b8c4ce'),
      borderRadius: 4,
      padding: csvFile ? 12 : 48,
      cursor: !csvFile ? 'pointer' : 'default',
      marginBottom: 16,
      backgroundColor: isDragging ? 'rgba(25,118,210,0.04)' : undefined
    },
    onClick: () => {
      var _fileInputRef$current;
      return !csvFile && ((_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 ? void 0 : _fileInputRef$current.click());
    },
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop
  }, !csvFile ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    align: "center",
    color: "light"
  }, "Upload a CSV to auto-detect unit names (optional)") : /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 12
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "file-text"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontWeight: 600
    }
  }, csvFile.name), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    size: "small",
    style: {
      minWidth: 0
    },
    onClick: e => {
      e.stopPropagation();
      handleRemoveFile();
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "trash"
  }))), /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    type: "file",
    accept: ".csv",
    hidden: true,
    onChange: handleFileInputChange
  })), csvHeaders.length > 0 && /*#__PURE__*/React.createElement(FormControl, {
    size: "small",
    fullWidth: true,
    error: showErrors && !isColumnValid,
    style: {
      marginBottom: 16
    }
  }, /*#__PURE__*/React.createElement(Select, {
    displayEmpty: true,
    value: unitNameColumn,
    onChange: e => handleColumnChange(e.target.value),
    renderValue: val => val ? val : /*#__PURE__*/React.createElement("span", {
      style: {
        color: '#9e9e9e'
      }
    }, "Select unit name column")
  }, csvHeaders.map(h => /*#__PURE__*/React.createElement(MenuItem, {
    key: h,
    value: h
  }, h))), showErrors && !isColumnValid && /*#__PURE__*/React.createElement(FormHelperText, null, "Required")), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      borderBottom: CELL_BORDER,
      paddingBottom: 8
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '0 12px 0 0',
      borderRight: CELL_BORDER
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600,
      color: '#6b7a8d'
    }
  }, "Livable unit names")), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '0 0 0 12px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600,
      color: '#6b7a8d'
    }
  }, "CSV unit names"))), unitNames.map((unitName, idx) => {
    return /*#__PURE__*/React.createElement("div", {
      key: unitName,
      style: {
        display: 'flex',
        alignItems: 'center',
        borderBottom: idx < unitNames.length - 1 ? CELL_BORDER : undefined
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        padding: '8px 12px 8px 0',
        borderRight: CELL_BORDER
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, unitName)), /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        padding: '4px 0 4px 12px'
      }
    }, csvUnitOptions.length > 0 ? /*#__PURE__*/React.createElement(FormControl, {
      size: "small",
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Select, {
      displayEmpty: true,
      value: unitMappings[unitName] || '',
      onChange: e => setUnitMappings(prev => _objectSpread(_objectSpread({}, prev), {}, {
        [unitName]: e.target.value
      })),
      renderValue: val => val ? val : /*#__PURE__*/React.createElement("span", {
        style: {
          color: '#9e9e9e'
        }
      }, "Select unit")
    }, csvUnitOptions.filter(u => !Object.entries(unitMappings).some(_ref2 => {
      let _ref3 = _slicedToArray(_ref2, 2),
        n = _ref3[0],
        v = _ref3[1];
      return n !== unitName && v === u;
    })).map(u => /*#__PURE__*/React.createElement(MenuItem, {
      key: u,
      value: u
    }, u)))) : /*#__PURE__*/React.createElement(TextField, {
      size: "small",
      fullWidth: true,
      placeholder: "Unit name",
      value: unitMappings[unitName] || '',
      onChange: e => setUnitMappings(prev => _objectSpread(_objectSpread({}, prev), {}, {
        [unitName]: e.target.value
      }))
    })));
  }));
});